// src/game/onboarding.ts

export type OnboardingSection = "world-tour" | "how-to-play";

export type OnboardingStepType =
  | "message"           // Rafsby says something on title, tap to advance
  | "highlight-building" // Hand pointer on building, tap building to enter it
  | "building-message"  // Message shown on a building's page, tap to advance
  | "return-to-city"    // Auto-returns to title screen on advance
  | "highlight-phone"   // Message + phone pulses
  | "prompt-action"     // Message + wait for player action (e.g. tap Solo)
  | "tutorial-step";    // In-game tutorial step with Rafsby dialogue

export type GamePhaseTarget = "PROFILE" | "SHOP" | "VERSUS_LOBBY" | "TITLE";

export interface OnboardingStep {
  type: OnboardingStepType;
  message: string;
  /** Building key to highlight: "battle" | "solo" | "shop" | "lobby" | "apartment" */
  highlightBuilding?: string;
  /** Phase to navigate to when building is tapped */
  navigateTo?: GamePhaseTarget;
  /** Tutorial step to sync with: "SWAP" | "ROTATE" | "GROOVE" | "RADIAL" */
  tutorialStep?: string;
  /** Action that advances this step (e.g. "OPEN_SOLO_SELECTOR") */
  waitForAction?: string;
}

export const WORLD_TOUR_STEPS: OnboardingStep[] = [
  // Intro
  { type: "message", message: "Yo! Welcome to Block City. Let me show you around real quick." },

  // Apartment
  { type: "message", message: "Your APARTMENT. Check your rank, switch skins, see your best highscores.", highlightBuilding: "apartment" },

  // Shop
  { type: "message", message: "The SHOP. Buy skins with gems. Get gems with coins or tokens.", highlightBuilding: "shop" },

  // Lobby
  { type: "message", message: "The LOBBY. Create or join a private room. Share the code with a friend.", highlightBuilding: "lobby" },

  // Hearts
  { type: "highlight-phone", message: "See those hearts? You spend one each match. They refill over time so use em wisely." },

  // Solo
  { type: "message", message: "SOLO mode. Multiple game modes. Grind for coins. Doesn't affect your rank.", highlightBuilding: "solo" },

  // Battle
  { type: "message", message: "BATTLE. Ranked PvP. Win and your rank goes up. Lose and it drops.", highlightBuilding: "battle" },
  { type: "message", message: "Regular, Silver, Gold, Diamond. Climb the ladder and prove yourself.", highlightBuilding: "battle" },

  // Final — auto-start battle vs Rafsby
  { type: "message", message: "Let's do a practice round. Me and you. Ready?" },
];

export const HOW_TO_PLAY_STEPS: OnboardingStep[] = [
  { type: "tutorial-step", message: "Swap two blocks to match 3 or more!", tutorialStep: "SWAP" },
  { type: "tutorial-step", message: "Use the rotate buttons to spin the board!", tutorialStep: "ROTATE" },
  { type: "tutorial-step", message: "Fill the groove meter for bonus points!", tutorialStep: "GROOVE" },
  { type: "tutorial-step", message: "Hold for a RADIAL BLAST \u2014 clears everything around it!", tutorialStep: "RADIAL" },
  { type: "message", message: "Not bad! You're gonna fit in. Hit me up anytime." },
];

export interface OnboardingState {
  active: boolean;
  section: OnboardingSection;
  stepIndex: number;
  /** True during incoming call screen (before player accepts) */
  callPending: boolean;
  /** Shake animation timer for incoming call */
  callShakeTimer: number;
  /** Phase we navigated to from a building (so we can overlay Rafsby) */
  visitingPage?: GamePhaseTarget | null;
}

export function createOnboardingState(section: OnboardingSection): OnboardingState {
  return {
    active: true,
    section,
    stepIndex: 0,
    callPending: section === "world-tour",
    callShakeTimer: 0,
    visitingPage: null,
  };
}

export function getOnboardingSteps(section: OnboardingSection): OnboardingStep[] {
  return section === "world-tour" ? WORLD_TOUR_STEPS : HOW_TO_PLAY_STEPS;
}

export function getCurrentStep(state: OnboardingState): OnboardingStep | null {
  if (!state.active) return null;
  const steps = getOnboardingSteps(state.section);
  return steps[state.stepIndex] ?? null;
}

export function advanceOnboardingStep(state: OnboardingState): boolean {
  const steps = getOnboardingSteps(state.section);
  if (state.stepIndex + 1 >= steps.length) {
    state.active = false;
    return false;
  }
  state.stepIndex++;
  return true;
}

export function updateCallShake(state: OnboardingState, dt: number): void {
  if (state.callPending) {
    state.callShakeTimer += dt;
  }
}

/** Rafsby NPC data for friends list injection */
export const RAFSBY_NPC = {
  odId: "npc-rafsby",
  username: "Rafsby",
  level: 99,
  online: true,
  equippedSkin: "rafsby",
  isNpc: true,
} as const;

/** Rafsby identity for onboarding bot match */
export const RAFSBY_BOT_IDENTITY = {
  username: "rafsby",
  displayName: "Rafsby",
  equippedSkin: "rafsby",
  level: 99,
  userId: "npc-rafsby",
} as const;
